import "./styles.css";
import { createGame } from "./game/createGame";

type ChangelogEntry = {
  id: string;
  date: string;
  type: string;
  title: string;
  summary: string;
};

const appCandidate = document.querySelector<HTMLDivElement>("#app");
if (!appCandidate) throw new Error("Missing #app");
const app: HTMLDivElement = appCandidate;

function shell(content: string): string {
  return `
    <header class="topbar">
      <a class="brand" href="/" aria-label="Underfoot home">
        <span class="brand-mark">⌁</span>
        <span>UNDERFOOT</span>
      </a>
      <nav>
        <a href="/">Play</a>
        <a href="/changelog">Changelog</a>
        <a href="https://github.com/dunctk/underfoot" rel="noreferrer">GitHub</a>
      </nav>
    </header>
    ${content}
  `;
}

async function renderGame(): Promise<void> {
  app.innerHTML = shell(`
    <main class="game-page">
      <section class="game-shell">
        <canvas id="game-canvas" aria-label="Underfoot game"></canvas>
        <div class="hud hud-top">
          <span>Night <strong data-testid="night-status" id="night-status">1</strong></span>
          <span>Path <strong data-testid="path-points" id="path-points">1</strong></span>
          <span>Knots <strong id="knot-count">0</strong></span>
          <span>Scuffed <strong id="scuffed-count">0</strong></span>
        </div>
        <div class="hud hud-bottom">
          <span>WASD · walk</span>
          <span id="bed-prompt">Find the bed when you are ready to sleep</span>
        </div>
        <section class="intro-overlay" id="intro-overlay" aria-label="About Underfoot">
          <div class="intro-card">
            <p class="eyebrow">THE HOUSE REMEMBERS YOUR ROUTE</p>
            <h1>Walk somewhere often enough<br />and it starts to become real.</h1>
            <p class="lede">A tiny playable foundation for a larger spatial mystery. Move with WASD. Cross your own trail. Watch what the vacuum forgets.</p>
            <button type="button" class="begin-button" id="begin-roaming">Begin roaming</button>
            <p class="wasd-hint">WASD · walk&ensp;·&ensp;E · sleep at the bed</p>
          </div>
        </section>
      </section>

      <div class="below-fold">
        <section class="principle-grid" aria-label="Current game principles">
        <article>
          <span>01</span>
          <h2>Movement is construction</h2>
          <p>Your path is not decoration. Repetition will become architecture.</p>
        </article>
        <article>
          <span>02</span>
          <h2>Domestic objects are tools</h2>
          <p>The robot vacuum currently scuffs weak trails. Later, ordinary household friction becomes the puzzle language.</p>
        </article>
        <article>
          <span>03</span>
          <h2>Knowledge persists</h2>
          <p>Nights reset. The player should not. The deepest progression lives in what you understand.</p>
        </article>
        </section>
      </div>
    </main>
  `);

  const canvas = document.querySelector<HTMLCanvasElement>("#game-canvas");
  if (!canvas) throw new Error("Missing game canvas");

  const overlay = document.querySelector("#intro-overlay");
  const beginButton = document.querySelector<HTMLButtonElement>("#begin-roaming");
  const dismissIntro = () => overlay?.classList.add("is-hidden");
  beginButton?.addEventListener("click", dismissIntro);
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" || ["w", "a", "s", "d"].includes(event.key.toLowerCase())) dismissIntro();
  });

  await createGame(canvas, {
    night: document.querySelector("#night-status")!,
    points: document.querySelector("#path-points")!,
    knots: document.querySelector("#knot-count")!,
    scuffed: document.querySelector("#scuffed-count")!,
    prompt: document.querySelector("#bed-prompt")!,
  });
}

async function renderChangelog(): Promise<void> {
  const response = await fetch("/changelog.json", { cache: "no-store" });
  const entries = (await response.json()) as ChangelogEntry[];

  app.innerHTML = shell(`
    <main class="changelog-page">
      <section class="changelog-hero">
        <p class="eyebrow">A GAME THAT CHANGES A LITTLE EVERY DAY</p>
        <h1>Changelog</h1>
        <p>Player-visible changes to UNDERFOOT, newest first. Internal refactors stay internal.</p>
      </section>
      <section class="changelog-list">
        ${entries
          .map(
            (entry) => `
              <article class="change">
                <div class="change-meta">
                  <time datetime="${entry.date}">${new Date(entry.date + "T12:00:00Z").toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    timeZone: "UTC",
                  })}</time>
                  <span class="change-type">${entry.type}</span>
                </div>
                <div>
                  <h2>${entry.title}</h2>
                  <p>${entry.summary}</p>
                </div>
              </article>
            `,
          )
          .join("")}
      </section>
    </main>
  `);
}

if (location.pathname.startsWith("/changelog")) {
  void renderChangelog();
} else {
  void renderGame();
}
